import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Stethoscope, Loader2, CheckCircle2, FileText, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { getDoctorRequest, submitDoctorResponse, uploadPhoto, fileUrl } from "../lib/api";
import { Toaster } from "../components/ui/sonner";

export default function DoctorRespondPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [req, setReq] = useState(null);
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is missing its secure token.");
      setLoading(false);
      return;
    }
    getDoctorRequest(id, token)
      .then((d) => {
        setReq(d);
        if (d.already_reviewed) setDone(true);
      })
      .catch((e) => {
        setError(e?.response?.status === 403 ? "This link is invalid or has expired." : "We couldn't load this request.");
      })
      .finally(() => setLoading(false));
  }, [id, token]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    if (!files.length) return;
    setUploading(true);
    try {
      const out = [];
      for (const f of files) {
        const res = await uploadPhoto(f);
        if (res?.path) out.push({ path: res.path, name: res.name, sig: res.sig });
      }
      setPhotos((p) => [...p, ...out].slice(0, 3));
    } catch {
      toast.error("Upload failed. Try a smaller image or PDF.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!note.trim() && photos.length === 0) {
      toast.error("Please write your review note.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitDoctorResponse(id, token, note.trim(), photos);
      if (res.ok) {
        setDone(true);
        toast.success("Response submitted — the patient has been notified.");
      } else {
        toast.error("Could not submit your response.");
      }
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "This link has expired." : "Could not submit your response.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] px-4">
        <div className="max-w-md text-center bg-white border-2 border-slate-200 rounded-2xl p-8" data-testid="doctor-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-heading font-bold text-xl text-slate-900 mb-2">Link problem</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-lg text-slate-900">AskMyDoctor AI — Physician Review</span>
        </div>

        <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 mb-5" data-testid="doctor-request-card">
          <span className={`inline-flex items-center gap-1 font-bold text-xs px-2 py-0.5 rounded-full mb-3 ${req.tier === "lab_review" ? "bg-violet-100 text-violet-800" : "bg-sky-100 text-sky-800"}`}>
            {req.tier === "lab_review" ? <><FileText className="w-3 h-3" /> Lab review</> : <><Stethoscope className="w-3 h-3" /> Standard review</>}
          </span>
          <h1 className="font-heading font-bold text-xl text-slate-900 mb-2">Patient question</h1>
          <p className="text-slate-700 leading-relaxed">{req.question}</p>

          {Array.isArray(req.thread) && req.thread.some((m) => (m.attachments || []).length > 0) && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 mb-2">Attachments from the patient</p>
              <div className="flex flex-wrap gap-2" data-testid="patient-attachments">
                {req.thread.flatMap((m) => m.attachments || []).map((a, k) =>
                  /\.pdf$/i.test(a.path || a.name || "") ? (
                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700">
                      <FileText className="w-4 h-4 text-red-500" /> {a.name || "PDF"}
                    </a>
                  ) : (
                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer">
                      <img src={fileUrl(a.path, a.sig)} alt={a.name || "attachment"} className="max-h-40 rounded-lg border border-slate-200" />
                    </a>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {done ? (
          <div className="bg-white border-2 border-green-200 rounded-2xl p-8 text-center" data-testid="doctor-done">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Review submitted</h2>
            <p className="text-slate-600">Thank you, Dr. {req.physician_name}. The patient has been notified by email.</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-6" data-testid="doctor-response-form">
            <label className="block font-heading font-bold text-lg text-slate-900 mb-2">Your review note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={7}
              data-testid="doctor-note-input"
              placeholder="Write a clear, plain-language note for the patient. Remind them this is educational and not a substitute for in-person care."
              className="w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-xl px-4 py-3 text-slate-800 outline-none resize-y"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer">
                <input type="file" accept="image/*,application/pdf" multiple onChange={onUpload} className="hidden" data-testid="doctor-upload-input" />
                <span className="border-2 border-slate-200 hover:border-teal-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Attach file {photos.length > 0 ? `(${photos.length})` : ""}
                </span>
              </label>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="doctor-submit-btn"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-6 py-2.5 rounded-xl transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Submit review
              </button>
            </div>
          </div>
        )}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
